<template>
  <div class="mb-5">
    <Alert status="warning">
      <p
        class="text-base"
        v-html="
          $t(
            'Please complete the required guest information. Your tickets will be available for download once your details are added and the event organiser releases them.'
          )
        "></p>
    </Alert>
  </div>
  <form @submit.prevent="handleSubmit">
    <div v-for="(item, i) of bookingsStore.conditions" class="flex flex-col mb-5">
      <p class="font-bold">{{ getCategoryName(bookingorderId, item.ticket_id) }}</p>
      <div>
        <GuestDataDetails
          v-for="(guest, n) of item.guests"
          :key="`g-${item.ticket_id}-${n}`"
          :guestdata="guest"
          :bookingorder-id="bookingorderId"
          :ticket-id="item.ticket_id"
          :guest="n"
          :required-fields="getFieldsFor(item.ticket_id, n, guest)"
          @update-requirements="onUpdateRequirements"
          :form-values="form.values"
          @input="onChange()" />
      </div>
    </div>
    <div class="bg-white flex sticky bottom-0 border-t save-button hidden">
      <button
        class="mt-3 bg-primary ml-auto p-4 text-forecolor lg:px-4 lg:py-2 rounded hover:shadow disabled:bg-neutral-300 disabled:text-neutral-100 disabled:hover:shadow-none">
        {{ $t("Save") }}
      </button>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { GuestDataDetails } from "@xs2event/js-lib/components";
import { useBookingsStore } from "@/store/bookings.js";
import { computed, ref, watch } from "vue";
import { useMutation } from "@tanstack/vue-query";
import { useI18n } from "vue-i18n";
import { useToast } from "@xs2event/js-lib/toast";
import { api } from "@/helpers/api.js";
import { object, string, phone, useForm, date, mixed } from "@xs2event/js-lib/validation";
import dayjs from "dayjs";
import { Alert } from "@xs2event/js-lib/components";
import { useGuestRequirements, useGuestForm } from "@xs2event/js-lib";

const { t } = useI18n();
const bookingsStore = useBookingsStore();
const { addToast } = useToast();

const props = defineProps({
  bookingorder: { type: Object, required: true },
});

bookingsStore.fetchBookingOrderGuestdata(props.bookingorder.bookingorder_id);

const bookingorderId = computed(() => props.bookingorder.bookingorder_id);

// Requirements and schema/form from shared module
const { getFieldsFor, onUpdateRequirements } = useGuestRequirements(computed(() => bookingsStore.conditions));

const { form } = useGuestForm({
  conditions: computed(() => bookingsStore.conditions),
  getFieldsFor,
  t,
});

const onChange = () => {
  document.getElementsByClassName(`save-button`)[0].classList.remove("hidden");
};

const mutation = useMutation({
  mutationFn: async (values: any) => {
    await api.put(`bookingorders/${bookingorderId.value}/guestdata`, { ...values });
  },
  onSuccess: async () => {
    addToast({ title: "Success", content: `Guest details successfully saved`, duration: 3000 });
    await bookingsStore.getTicketsForBranded(localStorage.booking_id);
  },
});

const handleSubmit = async () => {
  await form.handleSubmit(async (values: any) => {
    const itemsArray = Object.entries(values.items ?? {}).map(([ticket_id, guestsObj]) => {
      const guests = Object.values(guestsObj as Record<string, any>).map((g) => {
        const guest = { ...g };
        if (guest.date_of_birth) {
          const d = dayjs(guest.date_of_birth as any);
          if (d.isValid()) guest.date_of_birth = d.format("YYYY-MM-DD");
        }
        return guest;
      });
      return { ticket_id, guests };
    });
    const payload = { ...values, items: itemsArray };
    await mutation.mutateAsync(payload);
  })();
};

const getCategoryName = (bookingorderId: string, ticketId: string) => {
  const bookingorder = bookingsStore.bookingorders.find((bo) => bo.bookingorder_id === bookingorderId);
  const ticket = bookingorder.items.find((x) => x.ticket_id === ticketId);
  return ticket?.category_name || ticket?.ticket_id || ticketId;
};
</script>
